import React, { useState } from 'react';
import styled from 'styled-components';
import { SharedLayoutUltimate } from '../components/layout/SharedLayoutUltimate';
import { useCalendlyPopup } from '../hooks/useCalendlyPopup';
import {
  PageWrapper,
  HeroSection,
  Container,
  PageTitle,
  PageSubtitle,
  PremiumCard,
  PrimaryButton,
  SecondaryButton,
  FormGroup,
  Label,
  Input,
  Textarea,
  Select,
  TwoColumnGrid,
  Section,
  InfoBox,
  float
} from '../styles/PremiumStyles';
import { theme } from '../styles/theme';

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  margin-top: -50px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 30px;
    margin-top: -30px;
  }
`;

const ContactCard = styled(PremiumCard)`
  background: white;
  
  h3 {
    font-size: 28px;
    color: #1a2332;
    margin-bottom: 25px;
    font-weight: 800;
  }
`;

const ContactMethod = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 25px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  border: 2px solid rgba(255, 215, 0, 0.2);
  border-radius: 20px;
  margin-bottom: 20px;
  transition: all 0.3s;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
    border-color: rgba(255, 215, 0, 0.4);
  }
  
  .icon-wrapper {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #ffd700 0%, #ffa500 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    box-shadow: 0 5px 15px rgba(255, 215, 0, 0.3);
  }
  
  .content {
    flex: 1;
    
    .label {
      font-size: 14px;
      color: #666;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 5px;
    }
    
    .value {
      font-size: 20px;
      color: #1a2332;
      font-weight: 700;
    }
    
    .note {
      font-size: 13px;
      color: #999;
      margin-top: 3px;
    }
  }
`;

const MapContainer = styled.div`
  width: 100%;
  height: 400px;
  background: linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%);
  border-radius: 20px;
  margin-top: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 18px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 20px;
  }
`;

const TeamMember = styled.div`
  display: flex;
  gap: 25px;
  padding: 30px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%);
  border: 2px solid rgba(255, 215, 0, 0.2);
  border-radius: 20px;
  margin-bottom: 20px;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
  
  .avatar {
    width: 100px;
    height: 100px;
    background: linear-gradient(135deg, #ffd700 0%, #ffa500 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 42px;
    flex-shrink: 0;
  }
  
  .info {
    flex: 1;
    
    h4 {
      font-size: 22px;
      color: #1a2332;
      margin-bottom: 5px;
      font-weight: 700;
    }
    
    .title {
      font-size: 16px;
      color: #10a37f;
      font-weight: 600;
      margin-bottom: 10px;
    }
    
    .bio {
      font-size: 15px;
      color: #666;
      line-height: 1.6;
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    text-align: center;
    
    .avatar {
      margin: 0 auto;
    }
  }
`;

const FAQSection = styled.div`
  margin-top: 60px;
`;

const FAQItem = styled.div`
  margin-bottom: 20px;
  padding: 25px;
  background: white;
  border-radius: 15px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
  
  &:hover {
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  }
  
  h4 {
    font-size: 18px;
    color: #1a2332;
    margin-bottom: 10px;
    font-weight: 700;
  }
  
  p {
    font-size: 16px;
    color: #666;
    line-height: 1.6;
  }
`;

const SMSConsentWrapper = styled.div`
  margin-top: 20px;
  padding: 20px;
  background: rgba(255, 215, 0, 0.05);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 12px;
`;

const SMSConsentLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  line-height: 1.5;

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    margin-top: 2px;
    cursor: pointer;
    accent-color: #ffd700;
    flex-shrink: 0;
  }
`;

const OfficeHours = styled.div`
  background: linear-gradient(135deg, rgba(16, 163, 127, 0.1) 0%, rgba(16, 163, 127, 0.05) 100%);
  border: 2px solid rgba(16, 163, 127, 0.3);
  border-radius: 20px;
  padding: 30px;
  margin-top: 30px;
  
  h4 {
    font-size: 22px;
    color: #10a37f;
    margin-bottom: 20px;
    font-weight: 700;
    text-align: center;
  }
  
  .hours-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
    
    .day {
      display: flex;
      justify-content: space-between;
      padding: 10px;
      background: white;
      border-radius: 10px;
      
      .name {
        font-weight: 600;
        color: #1a2332;
      }
      
      .time {
        color: #666;
      }
      
      &.closed {
        opacity: 0.6;
        
        .time {
          color: #999;
        }
      }
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    .hours-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export const ContactUltimate: React.FC = () => {
  const { openCalendly } = useCalendlyPopup();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    preferredContact: 'email',
    smsConsent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Anti-spam fields
  const [honeypot, setHoneypot] = useState('');
  const [formLoadTime] = useState(Date.now());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Anti-spam validation
    if (honeypot) {
      // Bot detected - honeypot field was filled
      console.warn('Spam detected: honeypot field filled');
      return;
    }

    const timeSpent = Date.now() - formLoadTime;
    if (timeSpent < 3000) {
      // Submitted too quickly (less than 3 seconds)
      console.warn('Spam detected: form submitted too quickly');
      return;
    }

    setIsSubmitting(true);

    try {
      const emailData = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || 'Not provided',
        submission_type: 'contact_form',
        form_source: 'contact_page',
        subject: formData.subject,
        preferred_contact: formData.preferredContact,
        sms_consent: formData.smsConsent ? 'Yes' : 'No',
        formLoadTime: formLoadTime,
        website: honeypot,
        message: `
CONTACT FORM SUBMISSION
========================

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone || 'Not provided'}
Preferred Contact Method: ${formData.preferredContact}
SMS Consent: ${formData.smsConsent ? 'YES - Opted in to text messages' : 'NO - Did not opt in'}
Subject: ${formData.subject}

Message:
${formData.message}

Submitted: ${new Date().toLocaleString('en-US', { timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: true })}
        `.trim()
      };

      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: emailData }),
      });

      if (response.ok) {
        setIsSubmitting(false);
        setShowSuccess(true);
        // Reset form after 3 seconds
        setTimeout(() => {
          setShowSuccess(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            subject: '',
            message: '',
            preferredContact: 'email',
            smsConsent: false
          });
        }, 3000);
      } else {
        alert('There was an error submitting your message. Please try again.');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your message. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <SharedLayoutUltimate>
      <PageWrapper>
        <HeroSection style={{ color: 'white' }}>
          <Container>
            <PageTitle style={{ color: 'white' }}>
              Get in <span className="gold">Touch</span> With Us
            </PageTitle>
            <PageSubtitle style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
              We're here to help with all your annuity questions and concerns
            </PageSubtitle>
          </Container>
        </HeroSection>
        
        <Container>
          <ContactGrid>
            <ContactCard>
              <h3>Contact Information</h3>
              
              <ContactMethod onClick={() => window.location.href = 'tel:800-922-1894'}>
                <div className="icon-wrapper">📞</div>
                <div className="content">
                  <div className="label">Call Us</div>
                  <div className="value">800-922-1894</div>
                  <div className="note">Toll-free, Mon-Fri 9AM-5PM EST</div>
                </div>
              </ContactMethod>
              
              
              <ContactMethod onClick={openCalendly}>
                <div className="icon-wrapper">📅</div>
                <div className="content">
                  <div className="label">Schedule Consultation</div>
                  <div className="value">Book Online</div>
                  <div className="note">Free 30-minute consultation</div>
                </div>
              </ContactMethod>
              
              <ContactMethod>
                <div className="icon-wrapper">📍</div>
                <div className="content">
                  <div className="label">Office Location</div>
                  <div className="value">Toledo, Ohio</div>
                  <div className="note">Cap Averill II & Associates</div>
                </div>
              </ContactMethod>
              
              <OfficeHours>
                <h4>Office Hours</h4>
                <div className="hours-grid">
                  <div className="day">
                    <span className="name">Monday</span>
                    <span className="time">9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="day">
                    <span className="name">Tuesday</span>
                    <span className="time">9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="day">
                    <span className="name">Wednesday</span>
                    <span className="time">9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="day">
                    <span className="name">Thursday</span>
                    <span className="time">9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="day">
                    <span className="name">Friday</span>
                    <span className="time">9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="day closed">
                    <span className="name">Saturday</span>
                    <span className="time">Closed</span>
                  </div>
                  <div className="day closed">
                    <span className="name">Sunday</span>
                    <span className="time">Closed</span>
                  </div>
                  <div className="day">
                    <span className="name">Holidays</span>
                    <span className="time">Closed</span>
                  </div>
                </div>
              </OfficeHours>
            </ContactCard>
            
            <ContactCard>
              <h3>Send Us a Message</h3>
              
              {showSuccess && (
                <InfoBox $variant="success">
                  <h4>✅ Message Sent Successfully!</h4>
                  <p>Thank you for contacting us. We'll get back to you within 24 hours.</p>
                </InfoBox>
              )}
              
              <form onSubmit={handleSubmit}>
                <TwoColumnGrid>
                  <FormGroup>
                    <Label>Your Name <span className="required">*</span></Label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Email Address <span className="required">*</span></Label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                    />
                  </FormGroup>
                </TwoColumnGrid>
                
                <TwoColumnGrid>
                  <FormGroup>
                    <Label>Phone Number</Label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Preferred Contact Method</Label>
                    <Select
                      name="preferredContact"
                      value={formData.preferredContact}
                      onChange={handleChange}
                    >
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="either">Either</option>
                    </Select>
                  </FormGroup>
                </TwoColumnGrid>

                <SMSConsentWrapper>
                  <SMSConsentLabel>
                    <input
                      type="checkbox"
                      name="smsConsent"
                      checked={formData.smsConsent}
                      onChange={(e) => setFormData({ ...formData, smsConsent: e.target.checked })}
                    />
                    <span>
                      By providing your number, you are agreeing to receive text messages from Cap Averill II & Associates. This includes automated reminders, marketing messages, and general inquiries. Message frequency will vary. Msg & data rates may apply. Reply HELP for help or STOP to opt-out.
                    </span>
                  </SMSConsentLabel>
                </SMSConsentWrapper>

                <FormGroup>
                  <Label>Subject <span className="required">*</span></Label>
                  <Input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Message <span className="required">*</span></Label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Please provide as much detail as possible about your inquiry..."
                    required
                    style={{ minHeight: '200px' }}
                  />
                </FormGroup>

                {/* Honeypot field - hidden from users, catches bots */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                  <label htmlFor="website">Website</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div style={{ display: 'flex', gap: '20px', marginTop: '30px' }}>
                  <PrimaryButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </PrimaryButton>
                  <SecondaryButton type="button" onClick={openCalendly}>
                    Schedule Call Instead
                  </SecondaryButton>
                </div>
              </form>
            </ContactCard>
          </ContactGrid>
          
          <Section style={{ padding: '60px 0' }}>
            <h2 style={{ 
              fontSize: '36px', 
              textAlign: 'center', 
              marginBottom: '50px',
              color: '#1a2332',
              fontWeight: '800'
            }}>
              Meet Your Annuity Experts
            </h2>
            
            <TeamMember>
              <div className="avatar">👨‍💼</div>
              <div className="info">
                <h4>Cap Averill II</h4>
                <div className="title">Founder & Senior Annuity Advisor</div>
                <div className="bio">
                  With over 40 years of experience in the annuity industry, Cap has helped more than 10,000 clients 
                  secure their retirement futures. His commitment to transparency and client education has earned him 
                  an A+ BBB rating with zero complaints.
                </div>
              </div>
            </TeamMember>
          </Section>
          
          <FAQSection>
            <h2 style={{ 
              fontSize: '36px', 
              textAlign: 'center', 
              marginBottom: '40px',
              color: '#1a2332',
              fontWeight: '800'
            }}>
              Frequently Asked Questions
            </h2>
            
            <FAQItem>
              <h4>Do you charge fees for your services?</h4>
              <p>No, all our services are completely free.</p>
            </FAQItem>
            
            <FAQItem>
              <h4>How quickly will I receive a response?</h4>
              <p>We respond to all inquiries within 24 business hours. For urgent matters, please call us directly at 800-922-1894.</p>
            </FAQItem>
            
            <FAQItem>
              <h4>Is my information kept confidential?</h4>
              <p>Absolutely. Your personal information is never shared or sold to third parties. We maintain strict confidentiality for all our clients.</p>
            </FAQItem>
          </FAQSection>
          
          <MapContainer>
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d95544.89282748439!2d-83.62074842298087!3d41.65317964487734!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x883c7a5c5c5c5c5c%3A0x0!2sToledo%2C%20OH!5e0!3m2!1sen!2sus!4v1234567890"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </MapContainer>
        </Container>
      </PageWrapper>
    </SharedLayoutUltimate>
  );
};